import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    FormControl,
    Select,
    MenuItem,
    Fade,
    useTheme,
    useMediaQuery,
    Collapse
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    ExpandMore as ExpandMoreIcon,
    SportsEsports as GamepadIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import { isUserSuperAdmin } from '../../services/tracker-limited-data';
import DeckStatsModal from './DeckStatsModal';
import { useNavigate } from 'react-router-dom';
import { getAllInkColors } from './InkColorIcon';
import DeckVisual from './DeckVisual';

const DeckListItem = ({
    deck,
    currentUser,
    isDeckFavorite,
    getUserDeckCategory,
    getCategoryColor,
    getCategoryLabel,
    onToggleFavorite,
    onCopyDeckList,
    onEditDeck,
    onDeleteDeck,
    onCategoryChange,
    onImageClick,
    onOpenQuickMatch
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [statsModalOpen, setStatsModalOpen] = useState(false);
    const inkColors = getAllInkColors();

    // Sprawdź czy użytkownik jest superadministratorem
    useEffect(() => {
        const checkSuperAdminStatus = async () => {
            if (currentUser) {
                try {
                    const superAdminStatus = await isUserSuperAdmin(currentUser.uid);
                    setIsSuperAdmin(superAdminStatus);
                } catch (error) {
                    console.error('Błąd podczas sprawdzania uprawnień superadministratora:', error);
                    setIsSuperAdmin(false);
                }
            } else {
                setIsSuperAdmin(false);
            }
        };

        checkSuperAdminStatus();
    }, [currentUser]);

    // Sprawdź czy użytkownik może edytować/usunąć talię
    const canEditDeck = currentUser && (deck.author === currentUser.uid || isSuperAdmin);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const handleStatsClick = (e) => {
        e.stopPropagation();
        navigate(`/tracker/${deck.id}`);
    };

    const handleDeckNameClick = (e) => {
        e.stopPropagation();
        navigate(`/tracker/${deck.id}`);
    };

    const getInkColor = (color) => {
        const colorData = inkColors.find(c => c.value === color);
        return colorData?.color || '#666';
    };

    // Funkcja pomocnicza do wyświetlania statystyk talii
    const renderDeckStats = (deckStats, isCompact = false) => {
        if (!deckStats || !deckStats.overall) return null;

        const { overall } = deckStats;
        const winrateColor = overall.winrate >= 60 ? '#10b981' : overall.winrate >= 50 ? '#f59e0b' : '#ef4444';

        if (isCompact) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        '&:hover': {
                            opacity: 0.8
                        }
                    }}
                    onClick={handleStatsClick}
                >
                    <Chip
                        label={`${overall.winrate}%`}
                        size="small"
                        sx={{
                            backgroundColor: `${winrateColor}20`,
                            color: winrateColor,
                            border: `1px solid ${winrateColor}40`,
                            fontSize: '0.65rem',
                            height: 18,
                            fontWeight: 600
                        }}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.65rem' }}>
                        {overall.games} gier
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    cursor: 'pointer',
                    p: 1,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(17, 24, 39, 0.3)',
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    '&:hover': {
                        backgroundColor: 'rgba(17, 24, 39, 0.5)',
                        border: '1px solid rgba(76, 29, 149, 0.4)'
                    }
                }}
                onClick={handleStatsClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon sx={{ color: '#7c3aed', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                        Statystyki talii (kliknij aby zobaczyć szczegóły)
                    </Typography>
                </Box>

                {/* Ikony kolorów atramentu talii */}
                {deck.archetype?.ink_colors && deck.archetype.ink_colors.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.3, mb: 0.5 }}>
                        {deck.archetype.ink_colors.map((color, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: getInkColor(color),
                                    border: `1px solid ${getInkColor(color)}`
                                }}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label={`${overall.winrate}% WR`}
                        size="small"
                        sx={{
                            backgroundColor: `${winrateColor}20`,
                            color: winrateColor,
                            border: `1px solid ${winrateColor}40`,
                            fontSize: '0.65rem',
                            height: 18
                        }}
                    />
                    <Chip
                        label={`${overall.wins}W`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(16, 185, 129, 0.2)',
                            color: '#10b981',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            fontSize: '0.65rem',
                            height: 18
                        }}
                    />
                    <Chip
                        label={`${overall.losses}L`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            fontSize: '0.65rem',
                            height: 18
                        }}
                    />
                </Box>
            </Box>
        );
    };

    if (isSmallMobile) {
        // Widok mobilny - kompaktowy z możliwością rozwijania
        return (
            <>
                <Fade in={true} timeout={300}>
                    <Box sx={{
                        backgroundColor: 'rgba(17, 24, 39, 0.3)',
                        border: '1px solid rgba(76, 29, 149, 0.2)',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(17, 24, 39, 0.5)',
                            border: '1px solid rgba(76, 29, 149, 0.4)'
                        }
                    }}>
                        {/* Główny rząd - zawsze widoczny */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1.5,
                            gap: 1.5
                        }}>
                            {/* Mini podgląd grafiki talii */}
                            <Box sx={{
                                width: 110,
                                height: 54,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '2px solid rgba(124, 58, 237, 0.3)',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                                '&:hover': {
                                    border: '2px solid rgba(124, 58, 237, 0.6)',
                                    transform: 'scale(1.05)'
                                }
                            }}
                                onClick={() => onImageClick(deck)}
                            >
                                <DeckVisual deck={deck} mode="thumbnail" />
                            </Box>

                            {/* Nazwa talii - elastyczna */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        lineHeight: 1.2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        mb: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: '#c4b5fd'
                                        }
                                    }}
                                    onClick={handleDeckNameClick}
                                >
                                    {deck.name}
                                </Typography>
                                {/* Statystyki talii - kompaktowe */}
                                {renderDeckStats(deck.stats, true)}
                            </Box>

                            {/* Przycisk rozwijania */}
                            <IconButton
                                size="small"
                                onClick={handleExpandClick}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.7)',
                                    width: 24,
                                    height: 24,
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>

                        {/* Rozwijana sekcja z dodatkowymi informacjami */}
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{
                                px: 1.5,
                                pb: 1.5,
                                borderTop: '1px solid rgba(76, 29, 149, 0.2)',
                                mt: 1
                            }}>
                                {/* Status użytkownika */}
                                <Box sx={{ mb: 2 }}>
                                    {currentUser ? (
                                        <FormControl size="small" sx={{ width: '100%' }}>
                                            <Select
                                                value={getUserDeckCategory(deck.id)}
                                                onChange={(e) => onCategoryChange(deck.id, e.target.value)}
                                                sx={{
                                                    color: getCategoryColor(getUserDeckCategory(deck.id)),
                                                    fontSize: '0.8rem',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none'
                                                    },
                                                    '& .MuiSelect-select': {
                                                        padding: '8px 12px',
                                                        backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}20`,
                                                        borderRadius: '10px',
                                                        border: `1px solid ${getCategoryColor(getUserDeckCategory(deck.id))}40`
                                                    },
                                                    '&:hover .MuiSelect-select': {
                                                        backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}30`
                                                    }
                                                }}
                                            >
                                                <MenuItem value="unknown">Nieznana</MenuItem>
                                                <MenuItem value="to_test">Do przetestowania</MenuItem>
                                                <MenuItem value="rejected">Odrzucona</MenuItem>
                                                <MenuItem value="selected">Wybrana</MenuItem>
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Chip
                                            label={getCategoryLabel('unknown')}
                                            size="small"
                                            sx={{
                                                backgroundColor: `${getCategoryColor('unknown')}20`,
                                                color: getCategoryColor('unknown'),
                                                border: `1px solid ${getCategoryColor('unknown')}40`,
                                                fontSize: '0.8rem'
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Statystyki talii - szczegółowe */}
                                {renderDeckStats(deck.stats, false)}

                                {/* Przyciski akcji */}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {/* Przycisk meczu */}
                                    {onOpenQuickMatch && (
                                        <Tooltip title="Dodaj mecz">
                                            <IconButton
                                                size="small"
                                                onClick={() => onOpenQuickMatch(deck.id)}
                                                sx={{
                                                    color: 'rgba(34, 197, 94, 0.7)',
                                                    '&:hover': {
                                                        color: '#22c55e',
                                                        backgroundColor: 'rgba(34, 197, 94, 0.1)'
                                                    }
                                                }}
                                            >
                                                <GamepadIcon sx={{ fontSize: 22 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Przycisk ulubionych */}
                                    {currentUser && (
                                        <Tooltip title={isDeckFavorite(deck.id) ? "Usuń z ulubionych" : "Dodaj do ulubionych"}>
                                            <IconButton
                                                size="small"
                                                onClick={() => onToggleFavorite(deck.id)}
                                                sx={{
                                                    color: isDeckFavorite(deck.id) ? '#f59e0b' : 'rgba(196, 181, 253, 0.7)',
                                                    '&:hover': {
                                                        color: isDeckFavorite(deck.id) ? '#f59e0b' : '#f59e0b',
                                                        backgroundColor: 'rgba(245, 158, 11, 0.1)'
                                                    }
                                                }}
                                            >
                                                {isDeckFavorite(deck.id) ? (
                                                    <FavoriteIcon sx={{ fontSize: 22 }} />
                                                ) : (
                                                    <FavoriteBorderIcon sx={{ fontSize: 22 }} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Przycisk kopiowania */}
                                    <Tooltip title="Kopiuj listę kart">
                                        <IconButton
                                            size="small"
                                            onClick={() => onCopyDeckList(deck)}
                                            sx={{
                                                color: 'rgba(196, 181, 253, 0.7)',
                                                '&:hover': {
                                                    color: '#c4b5fd',
                                                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                                }
                                            }}
                                        >
                                            <CopyIcon sx={{ fontSize: 22 }} />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Przyciski edycji i usuwania - dla własnych talii lub administratorów */}
                                    {canEditDeck && (
                                        <>
                                            <Tooltip title="Edytuj talię">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEditDeck(deck)}
                                                    sx={{
                                                        color: 'rgba(196, 181, 253, 0.7)',
                                                        '&:hover': {
                                                            color: '#c4b5fd',
                                                            backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: 22 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Usuń talię">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onDeleteDeck(deck.id)}
                                                    sx={{
                                                        color: 'rgba(239, 68, 68, 0.7)',
                                                        '&:hover': {
                                                            color: '#ef4444',
                                                            backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 22 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                </Fade>

                {/* Modal ze statystykami talii */}
                <DeckStatsModal
                    open={statsModalOpen}
                    onClose={() => setStatsModalOpen(false)}
                    deck={deck}
                />
            </>
        );
    }

    if (isMobile) {
        // Widok tabletowy - dwurzędowy
        return (
            <>
                <Fade in={true} timeout={300}>
                    <Box sx={{
                        backgroundColor: 'rgba(17, 24, 39, 0.3)',
                        border: '1px solid rgba(76, 29, 149, 0.2)',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(17, 24, 39, 0.5)',
                            border: '1px solid rgba(76, 29, 149, 0.4)'
                        }
                    }}>
                        {/* Pierwszy rząd - podstawowe informacje */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1.5,
                            gap: 2
                        }}>
                            {/* Mini podgląd grafiki talii */}
                            <Box sx={{
                                width: 140,
                                height: 62,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                border: '2px solid rgba(124, 58, 237, 0.3)',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                                '&:hover': {
                                    border: '2px solid rgba(124, 58, 237, 0.6)',
                                    transform: 'scale(1.05)'
                                }
                            }}
                                onClick={() => onImageClick(deck)}
                            >
                                <DeckVisual deck={deck} mode="thumbnail" />
                            </Box>

                            {/* Nazwa talii */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        mb: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: '#c4b5fd'
                                        }
                                    }}
                                    onClick={handleDeckNameClick}
                                >
                                    {deck.name}
                                </Typography>
                                {/* Statystyki talii - kompaktowe */}
                                {renderDeckStats(deck.stats, true)}
                            </Box>

                            {/* Przyciski akcji - tylko najważniejsze */}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {/* Przycisk meczu */}
                                {onOpenQuickMatch && (
                                    <Tooltip title="Dodaj mecz">
                                        <IconButton
                                            size="small"
                                            onClick={() => onOpenQuickMatch(deck.id)}
                                            sx={{
                                                color: 'rgba(34, 197, 94, 0.7)',
                                                width: 28,
                                                height: 28,
                                                '&:hover': {
                                                    color: '#22c55e',
                                                    backgroundColor: 'rgba(34, 197, 94, 0.1)'
                                                }
                                            }}
                                        >
                                            <GamepadIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {currentUser && (
                                    <Tooltip title={isDeckFavorite(deck.id) ? "Usuń z ulubionych" : "Dodaj do ulubionych"}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onToggleFavorite(deck.id)}
                                            sx={{
                                                color: isDeckFavorite(deck.id) ? '#f59e0b' : 'rgba(196, 181, 253, 0.7)',
                                                width: 28,
                                                height: 28,
                                                '&:hover': {
                                                    color: isDeckFavorite(deck.id) ? '#f59e0b' : '#f59e0b',
                                                    backgroundColor: 'rgba(245, 158, 11, 0.1)'
                                                }
                                            }}
                                        >
                                            {isDeckFavorite(deck.id) ? (
                                                <FavoriteIcon sx={{ fontSize: 20 }} />
                                            ) : (
                                                <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Tooltip title="Kopiuj listę kart">
                                    <IconButton
                                        size="small"
                                        onClick={() => onCopyDeckList(deck)}
                                        sx={{
                                            color: 'rgba(196, 181, 253, 0.7)',
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                                color: '#c4b5fd',
                                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                            }
                                        }}
                                    >
                                        <CopyIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Drugi rząd - status i dodatkowe akcje */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            pb: 1.5,
                            borderTop: '1px solid rgba(76, 29, 149, 0.2)',
                            mt: 1
                        }}>
                            {/* Status użytkownika */}
                            <Box sx={{ flex: 1, maxWidth: 200 }}>
                                {currentUser ? (
                                    <FormControl size="small" sx={{ width: '100%' }}>
                                        <Select
                                            value={getUserDeckCategory(deck.id)}
                                            onChange={(e) => onCategoryChange(deck.id, e.target.value)}
                                            sx={{
                                                color: getCategoryColor(getUserDeckCategory(deck.id)),
                                                fontSize: '0.75rem',
                                                height: 24,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: 'none'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '2px 8px',
                                                    backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}20`,
                                                    borderRadius: '10px',
                                                    border: `1px solid ${getCategoryColor(getUserDeckCategory(deck.id))}40`
                                                },
                                                '&:hover .MuiSelect-select': {
                                                    backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}30`
                                                }
                                            }}
                                        >
                                            <MenuItem value="unknown">Nieznana</MenuItem>
                                            <MenuItem value="to_test">Do przetestowania</MenuItem>
                                            <MenuItem value="rejected">Odrzucona</MenuItem>
                                            <MenuItem value="selected">Wybrana</MenuItem>
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <Chip
                                        label={getCategoryLabel('unknown')}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${getCategoryColor('unknown')}20`,
                                            color: getCategoryColor('unknown'),
                                            border: `1px solid ${getCategoryColor('unknown')}40`,
                                            fontSize: '0.7rem',
                                            height: 20
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Dodatkowe przyciski akcji */}
                            {canEditDeck && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Edytuj talię">
                                        <IconButton
                                            size="small"
                                            onClick={() => onEditDeck(deck)}
                                            sx={{
                                                color: 'rgba(196, 181, 253, 0.7)',
                                                width: 28,
                                                height: 28,
                                                '&:hover': {
                                                    color: '#c4b5fd',
                                                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                                }
                                            }}
                                        >
                                            <EditIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Usuń talię">
                                        <IconButton
                                            size="small"
                                            onClick={() => onDeleteDeck(deck.id)}
                                            sx={{
                                                color: 'rgba(239, 68, 68, 0.7)',
                                                width: 28,
                                                height: 28,
                                                '&:hover': {
                                                    color: '#ef4444',
                                                    backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                                }
                                            }}
                                        >
                                            <DeleteIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Fade>

                {/* Modal ze statystykami talii */}
                <DeckStatsModal
                    open={statsModalOpen}
                    onClose={() => setStatsModalOpen(false)}
                    deck={deck}
                />
            </>
        );
    }

    // Widok desktopowy - oryginalny layout
    return (
        <>
            <Fade in={true} timeout={300}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    backgroundColor: 'rgba(17, 24, 39, 0.3)',
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(17, 24, 39, 0.5)',
                        border: '1px solid rgba(76, 29, 149, 0.4)'
                    }
                }}>
                    {/* Lewa strona - informacje o talii */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {/* Mini podgląd grafiki talii */}
                        <Box sx={{
                            width: 180,
                            height: 72,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: '2px solid rgba(124, 58, 237, 0.3)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                border: '2px solid rgba(124, 58, 237, 0.6)',
                                transform: 'scale(1.05)'
                            }
                        }}
                            onClick={() => onImageClick(deck)}
                        >
                            <DeckVisual deck={deck} mode="thumbnail" />
                        </Box>

                        {/* Nazwa talii */}
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: '#c4b5fd'
                                    }
                                }}
                                onClick={handleDeckNameClick}
                            >
                                {deck.name}
                            </Typography>
                            {/* Statystyki talii - kompaktowe */}
                            {renderDeckStats(deck.stats, true)}
                        </Box>

                        {/* Status użytkownika */}
                        <Box sx={{ minWidth: 150 }}>
                            {currentUser ? (
                                <FormControl size="small" sx={{ minWidth: '100%' }}>
                                    <Select
                                        value={getUserDeckCategory(deck.id)}
                                        onChange={(e) => onCategoryChange(deck.id, e.target.value)}
                                        sx={{
                                            color: getCategoryColor(getUserDeckCategory(deck.id)),
                                            fontSize: '0.7rem',
                                            height: 20,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none'
                                            },
                                            '& .MuiSelect-select': {
                                                padding: '2px 8px',
                                                backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}20`,
                                                borderRadius: '10px',
                                                border: `1px solid ${getCategoryColor(getUserDeckCategory(deck.id))}40`
                                            },
                                            '&:hover .MuiSelect-select': {
                                                backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}30`
                                            }
                                        }}
                                    >
                                        <MenuItem value="unknown">Nieznana</MenuItem>
                                        <MenuItem value="to_test">Do przetestowania</MenuItem>
                                        <MenuItem value="rejected">Odrzucona</MenuItem>
                                        <MenuItem value="selected">Wybrana</MenuItem>
                                    </Select>
                                </FormControl>
                            ) : (
                                <Chip
                                    label={getCategoryLabel('unknown')}
                                    size="small"
                                    sx={{
                                        backgroundColor: `${getCategoryColor('unknown')}20`,
                                        color: getCategoryColor('unknown'),
                                        border: `1px solid ${getCategoryColor('unknown')}40`,
                                        fontSize: '0.7rem',
                                        height: 20
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Prawa strona - przyciski akcji */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Przycisk meczu */}
                        {onOpenQuickMatch && (
                            <Tooltip title="Dodaj mecz">
                                <IconButton
                                    size="small"
                                    onClick={() => onOpenQuickMatch(deck.id)}
                                    sx={{
                                        color: 'rgba(34, 197, 94, 0.7)',
                                        width: 28,
                                        height: 28,
                                        '&:hover': {
                                            color: '#22c55e',
                                            backgroundColor: 'rgba(34, 197, 94, 0.1)'
                                        }
                                    }}
                                >
                                    <GamepadIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Przycisk ulubionych - dla wszystkich użytkowników */}
                        {currentUser && (
                            <Tooltip title={isDeckFavorite(deck.id) ? "Usuń z ulubionych" : "Dodaj do ulubionych"}>
                                <IconButton
                                    size="small"
                                    onClick={() => onToggleFavorite(deck.id)}
                                    sx={{
                                        color: isDeckFavorite(deck.id) ? '#f59e0b' : 'rgba(196, 181, 253, 0.7)',
                                        width: 28,
                                        height: 28,
                                        '&:hover': {
                                            color: isDeckFavorite(deck.id) ? '#f59e0b' : '#f59e0b',
                                            backgroundColor: 'rgba(245, 158, 11, 0.1)'
                                        }
                                    }}
                                >
                                    {isDeckFavorite(deck.id) ? (
                                        <FavoriteIcon sx={{ fontSize: 20 }} />
                                    ) : (
                                        <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Przycisk kopiowania listy kart - dla wszystkich użytkowników */}
                        <Tooltip title="Kopiuj listę kart">
                            <IconButton
                                size="small"
                                onClick={() => onCopyDeckList(deck)}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.7)',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                        color: '#c4b5fd',
                                        backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                    }
                                }}
                            >
                                <CopyIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Przyciski edycji i usuwania - dla własnych talii lub administratorów */}
                        {canEditDeck && (
                            <>
                                <Tooltip title="Edytuj talię">
                                    <IconButton
                                        size="small"
                                        onClick={() => onEditDeck(deck)}
                                        sx={{
                                            color: 'rgba(196, 181, 253, 0.7)',
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                                color: '#c4b5fd',
                                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                            }
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Usuń talię">
                                    <IconButton
                                        size="small"
                                        onClick={() => onDeleteDeck(deck.id)}
                                        sx={{
                                            color: 'rgba(239, 68, 68, 0.7)',
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                                color: '#ef4444',
                                                backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </Box>
            </Fade>

            {/* Modal ze statystykami talii */}
            <DeckStatsModal
                open={statsModalOpen}
                onClose={() => setStatsModalOpen(false)}
                deck={deck}
            />
        </>
    );
};

export default DeckListItem;
